'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';


interface AboutGenCarbonSectionProps {
  currentLanguage: string;
}

const AboutGenCarbonSection = ({ currentLanguage }: AboutGenCarbonSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState<'founder' | 'solutions' | 'footprint' | 'focus'>('founder');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Use only English content
  const content = {
    en: {
      badge: 'A Trust for Environmental Efficiency as a Financial Advantage',
      title: 'GENCARBON ESG',
      subtitle: 'Founder-led | Trust-based | Technology × Sustainability × Finance',
      philosophy: 'If ESG does not change cash flows, cost of capital, or governance risk — it does not belong in decision-making.',
      mission: 'GenCarbon ESG was founded as a trust-based institution to enable breakthrough applications of technology for sustainability, where environmental impact, financial returns, and public trust must move together.',
      belief: 'We believe that small, well-designed nudges create large, systemic impact when applied at scale.',
      differentiator: 'Not a typical sustainability firm. A convergence platform where technology, finance, and ecology meet.',
      tabs: {
        founder: 'Founder',
        solutions: 'Core Solutions',
        footprint: 'Global Footprint',
        focus: '2026-2030 Focus'
      },
      founder: {
        title: 'FOUNDER',
        name: 'Dr. Nupur Mukherjee',
        expertise: 'AI • Quantum Computing • Sustainable Finance • Carbon Markets',
        credentialsTitle: 'FOUNDER TEAM CREDENTIALS (WHY TRUST THIS PLATFORM)',
        credentials: [
          'EHS since 2003 — British Petroleum',
          'SAP EHS & IoT emissions integration since 2006 (Oil & Gas SCADA)',
          'SCRA Certified',
          'CIMA (UK) | CPA Australia (ESG)',
          'Global Banker — UK, Europe, Asia-Pacific (Green Investments)',
          'AI & Spatial Intelligence — Advanced Research',
          'Quantum Computing — IISc Bengaluru',
          'Aerospace & Space Research — Technical University of Munich, Germany',
          'Carbon Credits & MRV Automation — enterprise & nature-based systems'
        ]
      },
      solutions: {
        title: 'CORE SOLUTIONS (TURNKEY & INVESTABLE)',
        items: [
          {
            title: 'Carbon Credits & MRV Automation',
            description: 'Nature-based, industrial, hybrid pathways with audit-ready data'
          },
          {
            title: 'Net Zero & Transition Economics',
            description: 'Abatement cost curves, capex realism, payback discipline'
          },
          {
            title: 'AI, Spatial & Drone Intelligence',
            description: 'Satellite, drone, IoT & ground-truthing for last-mile accuracy'
          },
          {
            title: 'Quantum-Inspired Portfolio Optimisation',
            description: 'Optimising carbon, water, land, and risk together'
          },
          {
            title: 'Regenerative & Blue Economy Solutions',
            description: 'Biochar, basalt rock erosion, water conservation, blue credits'
          }
        ]
      },
      footprint: {
        title: 'GLOBAL EXECUTION FOOTPRINT',
        subtitle: 'Europe • UK • USA • Asia-Pacific',
        regions: [
          {
            region: 'Finland',
            focus: 'Land & nature restoration'
          },
          {
            region: 'Germany (Rhine Valley)',
            focus: 'River basin & industrial sustainability'
          },
          {
            region: 'Southeast Asia',
            focus: 'Green finance & ESG capital deployment'
          },
          {
            region: 'India',
            focus: 'Biochar, carbon markets, farmland rejuvenation'
          }
        ]
      },
      focus: {
        title: '2026–2030 FOCUS AREAS',
        areas: [
          'Sustainable & low-carbon data centres (2026 onward)',
          'Advanced MRV & AI-led carbon markets',
          'Nature-based and hybrid carbon assets',
          'Space sustainability & space-debris ("space junk") problem-solving (2026–2030)'
        ],
        note: 'Sustainability challenges do not stop at Earth\'s surface.'
      },
      collaborative: {
        title: 'COLLABORATIVE BY DESIGN',
        description: 'GenCarbon ESG works independently and with collaborators — governments, enterprises, academics, technologists, and communities.',
        tagline: 'If the journey is long, we take it with friends.'
      },
      whyEngage: {
        title: 'WHY ENGAGE WITH GENCARBON ESG',
        points: [
          'Trust-led institution',
          'Audit-ready MRV',
          'Financially disciplined ESG',
          'Regulator-aligned (BRSR, Net Zero, NDCs)',
          'Designed for long-horizon missions'
        ]
      },
      cta: {
        title: 'CALL TO ACTION',
        subtitle: 'Boards • Governments • PSUs • Multilaterals • Enterprises',
        description: 'Build ESG that is investable, inspectable, and impactful.',
        button: 'Get Started',
        website: 'gencarbon.in'
      },
      microTagline: 'Small nudges. Big impact. Technology-led sustainability with financial integrity.'
    }
  };

  const lang = content.en; // Always use English

  if (!isHydrated) {
    return null;
  }

  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-800 text-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── JAN CARBON ORIGIN STORY & PROBLEM STATEMENT ── */}
        <div className="max-w-5xl mx-auto mb-16">
          {/* Trust Origin Banner */}
          <div className="bg-gradient-to-r from-amber-900/60 to-emerald-900/60 border border-amber-500/40 rounded-2xl p-8 mb-10 text-center">
            <div className="inline-flex items-center gap-2 px-5 py-2 bg-amber-500/20 rounded-full mb-5">
              <span className="text-amber-300 text-sm font-bold tracking-wide">🌿 A SUSTAINABILITY TRUST — IN MEMORY OF TYARAMMA</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
              GenCarbon / JAN Carbon
            </h2>
            <p className="text-lg text-amber-200 font-semibold mb-3">
              A Sustainability Trust inspired by Andhra Pradesh social reformer <span className="text-amber-300 font-bold">Tyaramma</span> — who fought for the wellbeing of ordinary people.
            </p>
            <p className="text-base text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Just as Tyaramma worked for the dignity and welfare of communities, GenCarbon / JAN Carbon works for <strong className="text-white">JAN Hith</strong> — the wellbeing of people, industry, and ecology — through affordable, AI-powered sustainability solutions.
            </p>
          </div>

          {/* The Problem — Layman Language */}
          <div className="bg-white/5 border border-red-500/30 rounded-2xl p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-red-400 text-xl">⚠️</span>
              </div>
              <h3 className="text-2xl font-bold text-red-300">The Problem — In Plain Language</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/5 rounded-xl p-5 border border-red-500/20">
                <p className="text-4xl mb-3">🌡️</p>
                <h4 className="font-bold text-white mb-2">Our Planet is Overheating</h4>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Factories, vehicles, and power plants release invisible gases (carbon dioxide, methane) that trap heat — like a blanket around the Earth. This is causing floods, droughts, and extreme weather that hurt farmers, cities, and communities.
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-5 border border-red-500/20">
                <p className="text-4xl mb-3">📋</p>
                <h4 className="font-bold text-white mb-2">Companies Must Now Report & Act</h4>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Indian law (BRSR) and global rules now require companies to measure, report, and reduce their carbon footprint. But most businesses — especially small vendors and farms — don't know where to start, and big consultants charge lakhs of rupees.
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-5 border border-red-500/20">
                <p className="text-4xl mb-3">💸</p>
                <h4 className="font-bold text-white mb-2">Sustainability Shouldn't Be Only for the Rich</h4>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Carbon credits, green certifications, and ESG reports are powerful tools — but they've been locked behind expensive consultants and complex jargon. Farmers, small businesses, and communities are left out of the income and opportunity they deserve.
                </p>
              </div>
            </div>
          </div>

          {/* The Solution — JAN Carbon */}
          <div className="bg-gradient-to-br from-emerald-900/50 to-teal-900/50 border border-emerald-500/40 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-emerald-400 text-xl">✅</span>
              </div>
              <h3 className="text-2xl font-bold text-emerald-300">GenCarbon / JAN Carbon — Our Answer</h3>
            </div>
            <p className="text-lg text-white font-semibold mb-6 leading-relaxed">
              We bring <strong className="text-emerald-300">AI, new-age technology, and globally proven sustainability tools</strong> to everyone — at affordable prices — so that ordinary people, businesses, and the environment all benefit together.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[
                { icon: '🤖', title: 'AI-Powered Reporting', desc: 'Automated sustainability reports that used to cost lakhs — now affordable for every business' },
                { icon: '🌱', title: 'Carbon Credit Income', desc: 'Farmers, land owners, and industries can earn real income by reducing carbon emissions' },
                { icon: '📊', title: 'Decarbonization Roadmap', desc: 'Step-by-step plan to reduce your carbon footprint and meet government requirements' },
                { icon: '🎓', title: 'Skill & Income Development', desc: 'Training and jobs in green economy for youth, communities, and professionals' },
              ].map((item, i) => (
                <div key={i} className="bg-white/10 rounded-xl p-4 border border-emerald-500/20">
                  <p className="text-3xl mb-2">{item.icon}</p>
                  <h4 className="font-bold text-white text-sm mb-1">{item.title}</h4>
                  <p className="text-slate-300 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="bg-white/5 rounded-xl p-5 border border-emerald-400/30">
              <p className="text-center text-emerald-200 font-semibold text-base">
                🤝 A <strong className="text-white">trusted ally for global government initiatives</strong> — aligned with India's Net Zero 2070, BRSR mandates, UN SDGs, and international carbon markets — working with enterprises, PSUs, governments, and communities.
              </p>
            </div>
          </div>
        </div>
        {/* ── END JAN CARBON ORIGIN STORY ── */}

        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-6 py-2 bg-emerald-500/20 rounded-full mb-6">
            <p className="text-sm font-semibold text-emerald-300">{lang.badge}</p>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">{lang.title}</h2>
          <p className="text-xl text-emerald-300 mb-6">{lang.subtitle}</p>
          <div className="max-w-4xl mx-auto space-y-4">
            <p className="text-lg text-slate-200 italic">"{lang.philosophy}"</p>
            <p className="text-base text-slate-300">{lang.mission}</p>
            <p className="text-base text-slate-300">{lang.belief}</p>
          </div>
        </div>

        {/* What Makes Different */}
        <div className="max-w-3xl mx-auto text-center mb-16 p-8 bg-white/5 backdrop-blur-sm rounded-2xl border border-emerald-500/30">
          <h3 className="text-2xl font-bold mb-4 text-emerald-300">WHAT MAKES GENCARBON ESG DIFFERENT</h3>
          <p className="text-lg text-slate-200 mb-6">(The Crossroads & Aggregation Model)</p>
          <div className="space-y-3 text-slate-300">
            <p>AI + Spatial Intelligence + Quantum-Inspired Optimisation</p>
            <p className="text-2xl">↓</p>
            <p>Carbon • Water • Land • Nature • Emissions</p>
            <p className="text-2xl">↓</p>
            <p>MRV Automation • Carbon Credits • Net Zero Execution</p>
            <p className="text-2xl">↓</p>
            <p className="text-emerald-300 font-semibold">Financial Benefits • Regulatory Trust • Community Impact</p>
          </div>
          <p className="mt-6 text-lg font-semibold text-white">{lang.differentiator}</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {Object.entries(lang.tabs).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as typeof activeTab)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                activeTab === key
                  ? 'bg-emerald-500 text-white shadow-lg scale-105'
                  : 'bg-white/10 text-slate-300 hover:bg-white/20'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="max-w-6xl mx-auto">
          {/* Founder Tab */}
          {activeTab === 'founder' && (
            <div className="space-y-8 animate-fadeIn">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-emerald-500/30">
                <div className="flex flex-col md:flex-row gap-8 items-start mb-8">
                  {/* Founder Info */}
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-4 text-emerald-300">{lang.founder.title}</h3>
                    <p className="text-lg text-slate-300 mb-4">{lang.founder.expertise}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Solutions Tab */}
          {activeTab === 'solutions' && (
            <div className="space-y-6 animate-fadeIn">
              <h3 className="text-3xl font-bold mb-8 text-center text-emerald-300">{lang.solutions.title}</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {lang.solutions.items.map((solution, index) => (
                  <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-emerald-500/30 hover:border-emerald-400 transition-all duration-300 hover:shadow-xl">
                    <h4 className="text-xl font-bold mb-3 text-white">{solution.title}</h4>
                    <p className="text-slate-300">{solution.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footprint Tab */}
          {activeTab === 'footprint' && (
            <div className="space-y-8 animate-fadeIn">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold mb-4 text-emerald-300">{lang.footprint.title}</h3>
                <p className="text-xl text-slate-200">{lang.footprint.subtitle}</p>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {lang.footprint.regions.map((item, index) => (
                  <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-emerald-500/30">
                    <div className="flex items-start space-x-4">
                      <Icon name="GlobeAltIcon" className="w-8 h-8 text-emerald-400 flex-shrink-0" />
                      <div>
                        <h4 className="text-xl font-bold mb-2 text-white">{item.region}</h4>
                        <p className="text-slate-300">{item.focus}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Focus Tab */}
          {activeTab === 'focus' && (
            <div className="space-y-8 animate-fadeIn">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-emerald-500/30">
                <h3 className="text-3xl font-bold mb-8 text-center text-emerald-300">{lang.focus.title}</h3>
                <div className="space-y-4 mb-6">
                  {lang.focus.areas.map((area, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Icon name="RocketLaunchIcon" className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-1" />
                      <p className="text-slate-200 text-lg">{area}</p>
                    </div>
                  ))}
                </div>
                <p className="text-center text-lg italic text-emerald-300 mt-8">{lang.focus.note}</p>
              </div>
            </div>
          )}
        </div>

        {/* Collaborative Section */}
        <div className="max-w-4xl mx-auto mt-16 text-center bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-emerald-500/30">
          <h3 className="text-2xl font-bold mb-4 text-emerald-300">{lang.collaborative.title}</h3>
          <p className="text-lg text-slate-200 mb-4">{lang.collaborative.description}</p>
          <p className="text-xl italic text-white font-semibold">"{lang.collaborative.tagline}"</p>
        </div>

        {/* Why Engage */}
        <div className="max-w-4xl mx-auto mt-16">
          <h3 className="text-3xl font-bold mb-8 text-center text-emerald-300">{lang.whyEngage.title}</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lang.whyEngage.points.map((point, index) => (
              <div key={index} className="flex items-center space-x-3 bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-emerald-500/30">
                <Icon name="CheckBadgeIcon" className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                <p className="text-slate-200">{point}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="max-w-4xl mx-auto mt-16 text-center bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-2xl p-12 shadow-2xl">
          <h3 className="text-3xl font-bold mb-4 text-white">{lang.cta.title}</h3>
          <p className="text-xl mb-4 text-emerald-100">{lang.cta.subtitle}</p>
          <p className="text-lg mb-8 text-white font-semibold">{lang.cta.description}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <Link
              href="/get-started"
              className="inline-flex items-center px-8 py-4 bg-white text-emerald-600 font-bold rounded-lg hover:bg-slate-100 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <Icon name="RocketLaunchIcon" className="w-5 h-5 mr-2" />
              {lang.cta.button}
            </Link>
            <a
              href="https://gencarbon.in"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-emerald-800 text-white font-bold rounded-lg hover:bg-emerald-700 transition-all duration-300"
            >
              <Icon name="GlobeAltIcon" className="w-5 h-5 mr-2" />
              {lang.cta.website}
            </a>
          </div>
        </div>

        {/* Micro Tagline */}
        <div className="text-center mt-12">
          <p className="text-sm text-slate-400 italic">{lang.microTagline}</p>
        </div>
      </div>
    </section>
  );
};

export default AboutGenCarbonSection;